<script setup lang="ts">
import IconItem from '@/components/Icon/IconItem.vue'
import { StackLayout } from '@/components/Layouts'
import { EColors } from '@/types/constants/ColorValues'
import type { DetectionModel } from '@/types/models/DetectionModel'
import { useDebounceFn } from '@vueuse/core'
import moment from 'moment'
import { computed, onMounted, onUpdated, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { TitleTypography } from '.'
import { EDebounceTime } from '../../utils/debounceUtils'
import TypographyItem from './TypographyItem.vue'

const { locale } = useI18n()

const DATETIME_FORMAT = computed(() =>
    locale.value === 'en' ? 'L HH:mm:ss.SS' : 'DD/MM/YYYY HH:mm:ss.SS'
)
const formatTimestamp = (date: Date | string) =>
    moment.utc(date).local().format(DATETIME_FORMAT.value)

type Props = DetectionModel & {
    index: number
}
const props = defineProps<Props>()

const generateTitle = () =>
    (title.value = `Bib ${props.bib} ${props.event_filter ? `- E${props.event_filter}` : ''} ${
        props.timer_filter ? `- T${props.timer_filter}` : ''
    }`)
const title = ref('')
const subtitle = computed(() => formatTimestamp(props.timestamp))

const generateTitleDelay = useDebounceFn(() => generateTitle(), EDebounceTime.DEFAULT)
onMounted(() => generateTitle())
onUpdated(() => {
    if (props.index === 0) {
        title.value = ''
        generateTitleDelay()
    } else generateTitle()
})
</script>

<template>
    <StackLayout>
        <IconItem icon="circle" :type="EColors.WHITE" class="small-icon" />
        <Transition :name="index === 0 ? 'slide-left' : ''">
            <TitleTypography
                v-if="title"
                :title="title"
                :subtitle="subtitle"
                class="detection-title"
                :gap="0">
                <template v-if="!!props.withdrawal" #title>
                    <StackLayout class="flex-wrap align-items-center" :gap="2">
                        <TypographyItem label="Bib" />
                        <TypographyItem
                            :label="bib"
                            :class="withdrawal ? `has-text-${EColors.DANGER}` : ''" />
                        <TypographyItem v-if="event_filter" :label="`- E${props.event_filter}`" />
                        <TypographyItem v-if="timer_filter" :label="`- T${props.timer_filter}`" />
                        <IconItem icon="flag" />
                    </StackLayout>
                </template>
            </TitleTypography>
        </Transition>
    </StackLayout>
</template>

<style scoped lang="scss">
.detection-title {
    & :deep(.title),
    :deep(.subtitle) {
        font-size: small;
    }
    & :deep(.subtitle) {
        color: var(--color-text-secondary);
    }
}
.small-icon {
    & :deep(svg) {
        width: 12px;
    }
}
</style>
